/**
 * KpiWidget — pojedyncza wartość (liczba / kwota / godziny) dla wybranej metryki + okresu,
 * z trendem względem poprzedniego okresu (chip ▲ +12,4 %) — wzór Figmy „08.6 · Cards & widgets".
 *
 * Dwa warianty wyglądu (`config.variant`):
 *  - `card` (domyślnie) — karta statystyki: wartość + chip trendu + podpis („vs. … w poprzednim
 *    okresie" albo „Aktualizacja dziś, 09:12"); nagłówek z ikoną i etykietą daje powłoka kafelka,
 *  - `tile` — kompaktowy kafelek: kafelek z ikoną + etykieta + wartość + chip, bez nagłówka.
 *
 * `period` przychodzi z kontenera (slot filtra w nagłówku kafelka); domyślnie `all`. Pod liczbą
 * (wariant `card`) widać ZAKRES: nazwę firmy, „cały tenant" (Firmy) albo „N firm" (suma w trybie
 * „Wszystkie firmy"). Scope firmy i uprawnienia rozstrzyga backend — 400/403 degraduje do
 * łagodnego stanu zamiast wywrotki.
 */

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useActiveCompanyScope } from '@/lib/use-active-company';

import { classifyDataError, fetchKpi } from '../dashboard-api';
import type { KpiMetric, WidgetRenderProps } from '../types';
import { DeltaChip, deltaPercent, Dot, formatKpiValue, IconTile, type Tone } from '../widget-ui';

import { WidgetDataError, WidgetEmpty, WidgetLoading } from './WidgetState';

/** Ikona i ton kafelka ikony per metryka (nazwy z `icon-map`). */
const METRIC_VISUAL: Record<KpiMetric, { icon: string; tone: Tone }> = {
  companies: { icon: 'Building2', tone: 'accent' },
  contractors: { icon: 'Contact', tone: 'purple' },
  employees: { icon: 'Users', tone: 'info' },
  products: { icon: 'Package', tone: 'warning' },
  purchase_invoices: { icon: 'Receipt', tone: 'danger' },
  sales_revenue: { icon: 'Wallet', tone: 'success' },
  documents_total: { icon: 'FileText', tone: 'accent' },
  avg_approval_hours: { icon: 'Clock', tone: 'warning' },
  active_users: { icon: 'Users', tone: 'info' },
};

/** Metryki, dla których spadek jest dobrą wiadomością (zielony chip przy spadku). */
const LOWER_IS_BETTER: ReadonlySet<KpiMetric> = new Set(['avg_approval_hours']);

export function metricIcon(metric: KpiMetric): string {
  return METRIC_VISUAL[metric].icon;
}

export function KpiWidget({ widget, period = 'all' }: WidgetRenderProps) {
  const { t } = useTranslation();
  const { me } = useTenantAuth();
  const { isAll, companyId, mode } = useActiveCompanyScope();

  // Hook bezwarunkowy — zawężenie typu poniżej steruje `enabled`.
  const metric = widget.type === 'kpi' ? widget.config.metric : null;
  const variant = widget.type === 'kpi' ? (widget.config.variant ?? 'card') : 'card';
  // Metryki tenant-wide (`companies`, `active_users`) liczą się bez X-Company-Id. Pozostałe w trybie
  // "Wszystkie firmy" sumują się po firmach usera (`scope=all`); bez żadnej firmy
  // (`mode === 'none'`) nie ma czego liczyć — zapytanie byłoby skazane na 400.
  const tenantWide = metric === 'companies' || metric === 'active_users';
  const sumsAcrossCompanies = metric !== null && !tenantWide && isAll;
  const blocked = metric !== null && !tenantWide && mode === 'none';

  // Scope firmy w kluczu — defense-in-depth: dziś przełącznik firmy odświeża
  // kafelki blankietowym invalidateQueries w CompanySwitcher, ale bez firmy w
  // queryKey przetrwały cache dałby dane poprzedniej firmy, gdyby to
  // invalidateQueries kiedyś zniknęło albo się prześcignęło z fetchem.
  const query = useQuery({
    queryKey: ['tenant', 'dashboard', 'kpi', metric, period, companyId ?? 'all'],
    queryFn: () => fetchKpi(metric!, period, sumsAcrossCompanies ? 'all' : 'company'),
    enabled: metric !== null && !blocked,
    staleTime: 60_000,
    retry: false,
  });

  if (widget.type !== 'kpi' || metric === null) return null;

  if (blocked) return <WidgetEmpty message={t('tenant.dashboard.pickCompany')} />;
  if (query.isLoading) return <WidgetLoading />;
  if (query.isError)
    return (
      <WidgetDataError kind={classifyDataError(query.error)} onRetry={() => query.refetch()} />
    );

  const data = query.data;
  const count = data?.count ?? 0;
  const unit = data?.unit ?? 'count';
  const formatted = formatKpiValue(count, unit, t);
  const delta = period === 'all' ? null : deltaPercent(count, data?.previous);
  const label = widget.config.title ?? t(`tenant.dashboard.metrics.${metric}`);
  const visual = METRIC_VISUAL[metric];

  if (variant === 'tile') {
    return (
      <div className="flex h-full items-center gap-3">
        <IconTile icon={widget.config.icon ?? visual.icon} tone={visual.tone} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs text-text-tertiary" title={label}>
            {label}
          </div>
          <div className="truncate text-xl font-semibold tabular-nums text-text-primary">
            {formatted}
          </div>
        </div>
        {delta !== null && <DeltaChip pct={delta} lowerIsBetter={LOWER_IS_BETTER.has(metric)} />}
      </div>
    );
  }

  // Podpis pod wartością: porównanie z poprzednim okresem albo czas aktualizacji.
  let caption: string;
  let captionDot = false;
  if (delta !== null && data?.previous !== null && data?.previous !== undefined) {
    caption = t('tenant.dashboard.kpi.vsPrevious', {
      value: formatKpiValue(data.previous, unit, t),
    });
  } else {
    captionDot = true;
    caption = t('tenant.dashboard.kpi.updated', {
      time: new Date(query.dataUpdatedAt || Date.now()).toLocaleTimeString('pl-PL', {
        hour: '2-digit',
        minute: '2-digit',
      }),
    });
  }

  let scopeLabel = '';
  if (tenantWide) {
    scopeLabel = t('tenant.dashboard.scope.tenant');
  } else if (sumsAcrossCompanies) {
    scopeLabel = t('tenant.dashboard.scope.companies', { count: data?.companies ?? 0 });
  } else {
    scopeLabel = me?.companies.find((c) => c.id === companyId)?.name ?? '';
  }

  return (
    <div className="flex h-full flex-col justify-center gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-3xl font-semibold tabular-nums text-text-primary">
          {formatted}
        </span>
        {delta !== null && <DeltaChip pct={delta} lowerIsBetter={LOWER_IS_BETTER.has(metric)} />}
      </div>
      <div className="flex items-center gap-1.5 text-xs text-text-tertiary">
        {captionDot && <Dot tone="accent" />}
        <span className="truncate">{caption}</span>
      </div>
      {scopeLabel && (
        <span
          className="max-w-full self-start truncate rounded-full bg-bg-tertiary px-2 py-0.5 text-[11px] text-text-secondary"
          title={scopeLabel}
        >
          {scopeLabel}
        </span>
      )}
    </div>
  );
}
